/**
 * Per-control placeholder opt-out — `ui.showPlaceholder`.
 *
 * Sheet templates supply placeholder text two different ways, and BOTH are
 * preserved verbatim here — this util adds an opt-out, it does not unify the
 * precedence rules (see *The two idioms* below).
 *
 * The reason an opt-out is needed at all: a **derived readonly** control has no
 * interaction to invite. Its value arrives from an upstream driver and the input
 * renders disabled, so `"Please select"` is a false instruction. The first such
 * control is `ssmt-dei:TypeOfSubmission` on role0 / role1 — `ui.readonly`, derived
 * from `Basis of accounting standards…`, and it legitimately renders EMPTY until
 * Basis is picked (see the `defaultValue` note in
 * `docs/specifications/sheets/00_filing-prerequisites.md` Field #18).
 *
 * ## The contract
 *
 * ```jsonc
 * "ui": { "control": "select", "readonly": true, "showPlaceholder": false }
 * ```
 *
 * **Default is `true`.** Omit the flag and nothing changes; only an explicit
 * `false` suppresses. That makes the flag additive for all ~700 existing controls.
 *
 * ## The two idioms
 *
 * Templates were written against two different precedence rules, and switching a
 * site from one to the other silently changes user-visible copy. So each has its
 * own entry point and its own pipe:
 *
 * | Template idiom | Use | Precedence |
 * |---|---|---|
 * | `control.ui.placeholder \|\| '0'` | `resolvePlaceholder(control, '0')` — pipe `placeholder` | schema's `ui.placeholder` wins; the argument is the fallback |
 * | `placeholder="Please select"` | `resolveFixedPlaceholder(control, 'Please select')` — pipe `fixedPlaceholder` | the argument wins; `ui.placeholder` is not consulted |
 *
 * The distinction is load-bearing, not cosmetic: role0/role1/role2's date controls
 * carry `ui.placeholder: "Select one"` while every `<p-datepicker>` site renders the
 * literal `"Select a date"`. Routing those through the schema-first form would
 * rewrite the copy on a dozen date fields — a change nobody asked for. Whether the
 * two idioms *should* be unified is a separate product question; when it is
 * answered, this file is the one place to change.
 *
 * ## Adopting it on a new sheet
 *
 * Add `PlaceholderPipe` / `FixedPlaceholderPipe` to the component's `imports` and
 * route each `placeholder` binding through the pipe matching its existing idiom.
 * Output stays byte-identical until some control sets the flag. A sheet whose
 * template has no placeholder binding at all (roles 8, 9, 12, 13, 15-410000, 17,
 * 18, 19) needs no change.
 */

/** The subset of a control's schema record this util reads. */
export interface PlaceholderDerivableControl {
  ui?: {
    placeholder?: string;
    showPlaceholder?: boolean;
  };
}

/** True when the control has explicitly opted out of showing a placeholder. */
function optedOut(control: PlaceholderDerivableControl | null | undefined): boolean {
  return control?.ui?.showPlaceholder === false;
}

/**
 * Schema-first placeholder — mirrors the `control.ui.placeholder || '<fallback>'`
 * template idiom. The schema value wins; `fallback` applies only when the schema
 * is silent.
 *
 * @returns `''` when the control sets `ui.showPlaceholder: false`.
 */
export function resolvePlaceholder(
  control: PlaceholderDerivableControl | null | undefined,
  fallback = ''
): string {
  if (optedOut(control)) return '';
  return control?.ui?.placeholder || fallback;
}

/**
 * Template-owned placeholder — mirrors a hard-coded `placeholder="…"` attribute.
 * `text` always wins and `ui.placeholder` is deliberately NOT consulted, because
 * these sites have never read it and several controls carry a different value there.
 *
 * @returns `''` when the control sets `ui.showPlaceholder: false`.
 */
export function resolveFixedPlaceholder(
  control: PlaceholderDerivableControl | null | undefined,
  text: string
): string {
  return optedOut(control) ? '' : text;
}
